import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "react-query";
import { useRouter } from "next/router";
import { useApi } from "./useApi";
import { useConnectedGithubOrgsNonSuspense } from "./useConnectedGithubOrgs";
import { ASSISTANT_SESSIONS_QUERY_KEY, fetchAssistantSessions } from "@/lib/assistant-sessions";
import { useLatestRef } from "@/hooks/useLatestRef";

/**
 * Prefetch Codesmith session list on mount so data is already cached
 * when the user navigates to Codesmith. Uses requestIdleCallback to
 * avoid competing with critical first-paint resources.
 *
 * The query key must match the one used by useSessionPools:
 * [ASSISTANT_SESSIONS_QUERY_KEY, orgName]. Otherwise react-query won't
 * consider the prefetched data a match for the consumer's useQuery and
 * will refetch. We also pass the resolved installation ID to
 * fetchAssistantSessions so the API response is scoped to the same org
 * the consumer expects — otherwise we'd cache cross-org data.
 */
export function usePrefetchAssistantSessions() {
  const $ = _c(11);
  const api = useApi();
  const queryClient = useQueryClient();
  const router = useRouter();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  const urlOrg = router.query.org as string | undefined;
  const orgName = urlOrg ?? connectedGithubOrgs?.active_org_name ?? null;
  let t0;
  bb0: {
    if (!connectedGithubOrgs?.installations || !orgName) {
      t0 = null;
      break bb0;
    }
    let t1;
    if ($[0] !== connectedGithubOrgs.installations || $[1] !== orgName) {
      let t2;
      if ($[3] !== orgName) {
        t2 = i => i.account.login.toLowerCase() === orgName.toLowerCase();
        $[3] = orgName;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      t1 = connectedGithubOrgs.installations.find(t2);
      $[0] = connectedGithubOrgs.installations;
      $[1] = orgName;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const inst = t1;
    t0 = inst?.blacksmith_id ?? null;
  }
  const currentInstallationId = t0;
  const apiRef = useLatestRef(api);
  let t1;
  let t2;
  if ($[5] !== apiRef || $[6] !== currentInstallationId || $[7] !== orgName || $[8] !== queryClient) {
    t1 = () => {
      if (!orgName || !currentInstallationId) {
        return;
      }
      const sessionQueryKey = [ASSISTANT_SESSIONS_QUERY_KEY, orgName];
      const doPrefetch = () => {
        const existing = queryClient.getQueryData(sessionQueryKey);
        if (existing) {
          return;
        }
        queryClient.prefetchQuery(sessionQueryKey, () => fetchAssistantSessions(apiRef.current, currentInstallationId, null, {
          excludeAutomationSessions: true
        }), {
          staleTime: 30000
        });
      };
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        const id = (window as Window).requestIdleCallback(doPrefetch, {
          timeout: 200
        });
        return () => (window as Window).cancelIdleCallback(id);
      } else {
        const id_0 = setTimeout(doPrefetch, 50);
        return () => clearTimeout(id_0);
      }
    };
    t2 = [queryClient, orgName, currentInstallationId, apiRef];
    $[5] = apiRef;
    $[6] = currentInstallationId;
    $[7] = orgName;
    $[8] = queryClient;
    $[9] = t1;
    $[10] = t2;
  } else {
    t1 = $[9];
    t2 = $[10];
  }
  useEffect(t1, t2);
}